<template>
  <div class="presets-delivery">
    <transition name="fade" mode="out-in">
      <div class="loader" v-if="!product" key="loader">
        <pn-spinner color="blue" size="large"></pn-spinner>
      </div>
      <div v-else>
        <delivery-option
          v-for="(option, i) in mainDeliveryOptions"
          :key="i"
          :option="option"
          :product="product"
          :showDescription="hasAvailableDeliveryOptions"
          :selectedDate="
            hasAvailableDeliveryOptions
              ? currentDate.format('YYYY-MM-DD')
              : currentDate.businessAdd(1).format('YYYY-MM-DD')
          "
          @optionSelected="optionSelected"
        />
      </div>
    </transition>

    <h1 class="bigshot spacing">
      {{ $t('W2P_DELIVERY_CHOOSE_ANOTHER_DATE') }}
    </h1>
    <pn-date-picker
      :label="$t('W2P_DELIVERY_DATE_LABEL')"
      :lang="lang"
      :min="minimumDate"
      :max="maximumDate"
      disable-weekends="true"
      :disable-days="holidays"
      position="top right"
      :value="selectedDate"
      @datechange="changeSelectedDate($event.target.value)"
    ></pn-date-picker>

    <div class="option-wrapper datepicker" v-if="product && selectedDate">
      <delivery-option
        v-for="(option, i) in customDeliveryOptions"
        :key="i"
        :option="option"
        :product="product"
        :showDescription="false"
        :selectedDate="selectedDate"
        :isCustomOption="true"
        @optionSelected="optionSelected"
      />
    </div>
  </div>
</template>

<script>
import DeliveryOption from '@/components/letter/delivery/DeliveryOptionComponent'
import { mapState, mapGetters, mapMutations } from 'vuex'
import axios from 'axios'
import moment from 'moment-business-days'

export default {
  components: { DeliveryOption },
  data: () => ({
    holidays: '',
    date: false,
    price: true,
    product: null,
    selectedOption: null,
    selectedDate: null,
    options: null
  }),
  computed: {
    ...mapState(['accessToken', 'lang', 'market', 'order']),
    ...mapGetters(['isBusinessUser']),
    ...mapGetters('letter/upload', [
      'getProductOptions',
      'getComputedNumberOfPages'
    ]),
    ...mapState('letter/address', [
      'recipientsData',
      'addressType',
      'targetType',
      'numberOfRecipients'
    ]),
    ...mapState('letter/delivery', ['deliveryOption', 'deliveryDate']),
    currentDate() {
      return moment()
    },
    maximumDate() {
      return moment().add(1, 'Y') //limit to only one year
    },
    minimumDate() {
      if (!this.product) {
        return null
      }

      const deliveryDays = this.product.pricingInfo.deliveryOptions.map(
        option => {
          return option.deliveryDays[0] ? option.deliveryDays[0].start : 0
        }
      )

      let amountOfDays = Math.min(...deliveryDays)

      if (!this.hasAvailableDeliveryOptions) {
        amountOfDays++
      }

      return moment()
        .businessAdd(amountOfDays)
        .format('YYYY-MM-DD')
    },
    availableDeliveryOptions() {
      return this.product.pricingInfo.deliveryOptions.filter(delivery => {
        return this.isOptionAvailable(delivery)
      })
    },
    hasAvailableDeliveryOptions() {
      return this.availableDeliveryOptions.length > 0
    },
    customDeliveryOptions() {
      return this.product.pricingInfo.deliveryOptions.filter(delivery => {
        return this.isCustomOptionAvailable(delivery)
      })
    },
    mainDeliveryOptions() {
      const options = this.hasAvailableDeliveryOptions
        ? this.availableDeliveryOptions
        : this.customDeliveryOptions

      return options.filter(
        option => !option.deliveryDays[0].description.includes('ECONOMY')
      )
    }
  },
  mounted() {
    this.selectedOption = this.deliveryOption

    this.getHolidays()

    if (this.getProductOptions) {
      this.options = this.getProductOptions
    }

    this.getProduct()
  },
  methods: {
    ...mapMutations('letter/delivery', [
      'SAVE_DELIVERY_ID',
      'SAVE_DATE',
      'SAVE_DELIVERY_OPTION'
    ]),
    setSelectedDate() {
      const deliveryDate = moment(this.deliveryDate)

      if (deliveryDate.isAfter(this.minimumDate)) {
        this.selectedDate = this.deliveryDate
      }
    },
    getHolidays() {
      axios
        .post(`${process.env.VUE_APP_W2P_API_BASE_URL}/pricing/holidays`, {
          params: {
            country: this.market,
            language: this.lang,
            year: moment().year()
          }
        })
        .then(({ data }) => {
          this.holidays = data.result
            .map(holiday => {
              return moment(holiday.date).format('YYYY-MM-DD')
            })
            .join() //format to comma separated dates
        })
    },
    getProduct() {
      axios
        .post(
          `${process.env.VUE_APP_W2P_API_BASE_URL}/clientAPI/products/pricing`,
          {
            productId: this.options.productId,
            country: 'SE',
            deliveryId: this.options.deliveryId,
            numberOfRecipients:
              this.numberOfRecipients ||
              this.options.recipientsData.recipientsCount,
            recipientsData: this.recipientsData,
            language: this.lang,
            userType: this.setUserType(),
            addressType: this.addressType,
            targetType: this.targetType,
            numberOfPages: this.getComputedNumberOfPages
          }
        )
        .then(({ data }) => {
          this.product = data
          this.setSelectedDate()
        })
    },
    isOptionAvailable(option) {
      let currentTime = moment()
      let optionTime = moment(option.deliveryDays[0].time, 'HH:mm')
      let available = currentTime.isBefore(optionTime)

      if (available && option.additionalServices) {
        available = option.additionalServices.every(service => {
          if (service.name === 'W2P_LATEHANDINTIME_NAME') {
            return this.numberOfRecipients <= service.lateLimit.max
          }

          return true
        })
      }

      return available
    },
    isCustomOptionAvailable(option) {
      const name = option.deliveryDays[0].description
      if (name.includes('BEFORESIX')) {
        return false
      }

      if (name.includes('ECONOMY')) {
        const current = moment().businessAdd(option.deliveryDays[0].start)._d
        const selectedDate = moment(this.selectedDate)

        return selectedDate.isAfter(current) && this.isBusinessUser
      }

      return true
    },
    optionSelected({ deliveryDate, deliveryId, value }) {
      this.SAVE_DATE(deliveryDate)
      this.SAVE_DELIVERY_ID(deliveryId)
      this.SAVE_DELIVERY_OPTION(value)

      this.selectedOption = value
    },
    changeSelectedDate(value) {
      this.selectedDate = value
    },
    setUserType() {
      if (this.isBusinessUser) {
        return 'company'
      } else {
        return 'public'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.spacing {
  padding-top: 2em;
}

pn-date-picker {
  margin-top: 2.5em;
  width: 100%;
}

@media screen and (max-width: 50em) {
  .presets-delivery {
    width: 100%;
  }

  pn-date-picker {
    width: 100%;
    margin-top: 0.5em;
  }

  pn-radio-button {
    max-width: 90%;
  }
}
</style>
